/**
 * Route definitions for the Developer Tools view — the tabbed shell plus one nested route per tab
 * (Slice 1: API Keys). The index redirects to the first tab so `/dashboard/developer-tools` is never
 * a dead end. Mounted only when the `developerTools` flag is on (see router.tsx).
 */
import React from "react";
import { Navigate, RouteObject } from "react-router-dom";
import { withHandle } from "../../utils/route.util";
import { createCredentialRoute } from "../api-credentials/routes";
import { DeveloperToolsView } from "./DeveloperToolsView";
import { DEVELOPER_TOOLS_TABS } from "./developerToolsTabs";
import { ApiKeysTab } from "./api-keys/ApiKeysTab";

interface TabContent {
  element: React.ReactNode;
  /** The Generate Key drawer mounts under its tab so it opens over the API Keys list. */
  children?: RouteObject[];
}

const TAB_CONTENT = new Map<string, TabContent>([
  ["api-keys", { element: <ApiKeysTab />, children: [createCredentialRoute] }],
]);

function contentForTab(path: string): TabContent {
  const content = TAB_CONTENT.get(path);
  if (!content) {
    throw new Error(`Developer Tools tab "${path}" has no content registered in TAB_CONTENT.`);
  }
  return content;
}

const tabRoutes: RouteObject[] = DEVELOPER_TOOLS_TABS.map((tab) => ({
  path: tab.path,
  ...contentForTab(tab.path),
  ...withHandle(tab.pageId, tab.requiredRole ? { requiredRole: tab.requiredRole } : undefined),
}));

export const developerToolsRoutes: RouteObject[] = [
  {
    path: "developer-tools",
    element: <DeveloperToolsView />,
    ...withHandle("developer-tools"),
    children: [
      {
        index: true,
        element: <Navigate to={DEVELOPER_TOOLS_TABS[0].path} replace />,
        ...withHandle("developer-tools-index"),
      },
      ...tabRoutes,
    ],
  },
];
